<template>
  <div>
    <h2 class="text-3xl">
      送信設定
    </h2>
    <vx-card
      v-if="reviewRequest"
      class="mt-6 relative"
    >
      <vs-button
        class="absolute top-0 right-0 m-2"
        icon="edit"
        type="border"
        @click="onClickEdit"
      />
      <review-request-form
        class="my-8 mx-4"
        :day="day"
        :hour="hour"
        :enabled="enabled"
        read-only
      />
    </vx-card>
    <vs-button
      v-else
      class="mt-6"
      @click="onClickRegister"
    >
      登録
    </vs-button>
    <template v-if="reportData.length > 0">
      <h2 class="text-3xl mt-6">
        送信数推移
      </h2>
      <div class="flex justify-end">
        <vs-button
          @click="onReviewRequestHistoryClick"
        >
          送信履歴
        </vs-button>
      </div>
      <vx-card class="mt-4">
        <review-request-chart
          :report="reportData"
          :height="400"
        />
      </vx-card>
    </template>
    <template v-if="reviewRequestErrors.length > 0">
      <h2 class="text-3xl mt-6">
        送信エラー一覧
      </h2>
      <vx-card class="mt-6">
        <data-table
          :column-defs="columnDefs"
          :default-col-def="defaultColDef"
          :row-data="reviewRequestErrors"
          :total="total"
          :current-page="currentPage"
          :pagination-page-size="pageSize"
          pagination
          @first-data-rendered="onFirstDataRender"
          @update:current-page="onUpdateCurrentPage"
          @update:pagination-page-size="onUpdatePaginationPageSize"
          @sort-changed="onSortChange"
        />
      </vx-card>
      <h2 class="text-3xl mt-6">
      エラー内容
      </h2>
      <div class="">
        <paragraph class="mt-4">・You can’t use this feature to request a review outside the 5-30 day range after the order delivery date.：ユーザーが商品を受け取ってから5日経っていない場合に本エラーが発生します。Ubun BASEでは本エラーが発生した場合は、成功するまで翌日にリトライをおこないます。
          ・You have already requested a review for this order.：本エラーはUbun BASE以外から既にレビューリクエストが送られている場合に発生します。レビューリクエストは1注文に対して1回までしか送ることができません。
        </paragraph>
      </div>
    </template>
    <form-modal
      v-model="showForm"
      title="レビューリクエスト送信設定"
      :submit-button-text="id ? '更新' : '登録'"
      @click:submit="onClickSubmit"
    >
      <template #content>
        <ValidationForm ref="reviewRequestObserver">
          <review-request-form
            v-model:day="day"
            v-model:hour="hour"
            v-model:enabled="enabled"
            class="my-8"
          />
        </ValidationForm>
      </template>
    </form-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import DataTable from '@/components/table/DataTable'
import FormModal from '@/components/modals/FormModal'
import ReviewRequestForm from '@/components/forms/ReviewRequestForm'
import ReviewRequestChart from '@/user/components/charts/ReviewRequestChart'
import Paragraph from '@/components/Paragraph'
import { debounce } from '@/utils'

export default {
  name: 'ReviewRequest',
  components: {
    Paragraph,
    ReviewRequestForm,
    ReviewRequestChart,
    FormModal,
    DataTable
  },
  data () {
    return {
      showForm: false,
      id: null,
      day: 14,
      hour: 15,
      enabled: false,
      sortParam: '',
      currentPage: 1,
      total: 0,
      pageSize: 50,
      defaultColDef: {
        sortable: true,
        editable: false,
        resizable: true,
        suppressMenu: true
      },
      columnDefs: [
        {
          headerName: '送信日時',
          field: 'reviewRequestDate'
        },
        {
          headerName: 'オーダーID',
          field: 'amazonOrderId'
        },
        {
          headerName: 'コード',
          field: 'code'
        },
        {
          headerName: 'メッセージ',
          field: 'message'
        },
        {
          headerName: '詳細',
          field: 'detail'
        }
      ]
    }
  },
  computed: {
    ...mapState('reviewRequest', ['reviewRequest', 'reviewRequestErrors', 'reportData'])
  },
  watch: {
    reviewRequest: {
      deep: true,
      immediate: true,
      handler (val) {
        if (val) {
          this.id = val.id
          this.day = val.day
          this.hour = val.hour
          this.enabled = !!val.enabled
        }
      }
    }
  },
  async mounted () {
    await this.getReviewRequest()
    await this.fetchReviewRequestErrors()
    // 直近90日
    await this.getReport(
      {
        start_day: moment().subtract(90, 'days').format().split('T')[0],
        end_day: moment().add(1, 'day').format().split('T')[0],
      }
    )
  },
  methods: {
    ...mapActions('reviewRequest', [
      'listReviewRequestErrors',
      'getReviewRequest',
      'addReviewRequest',
      'updateReviewRequest',
      'getReport',
      'downloadCSV'
    ]),
    onClickRegister () {
      this.showForm = true
    },
    onClickSubmit: debounce(async function () {
      try {
        const isValid = await this.$refs.reviewRequestObserver.validate()
        if (!isValid.valid) return
        if (this.id) {
          await this.updateReviewRequest({
            id: this.id,
            day: this.day,
            hour: this.hour,
            enabled: this.enabled
          })
        } else {
          await this.addReviewRequest({
            day: this.day,
            hour: this.hour,
            enabled: this.enabled
          })
        }
        this.showForm = false
      } catch (e) {
        console.error(e)
      }
    }, 1000),
    async onClickEdit () {
      this.showForm = true
    },
    onUpdateCurrentPage (val) {
      this.currentPage = val
      this.fetchReviewRequestErrors()
    },
    onUpdatePaginationPageSize (val) {
      this.currentPage = 1
      this.pageSize = val
      this.fetchReviewRequestErrors()
    },
    dateChange () {
      this.currentPage = 1
      this.fetchReviewRequestErrors()
    },
    onSortChange (e) {
      const sortModel = e.columnApi.getColumnState().find(s => s.sort != null)
      if (sortModel) {
        const sortParam = `${sortModel.colId}-${sortModel.sort}`
        if (this.sortParam !== sortParam) {
          this.currentPage = 1
        }
        this.sortParam = sortParam
        this.fetchReviewRequestErrors()
      }
    },
    async onReviewRequestHistoryClick () {
      try {
        // 直近90日
        const payload = {
          start_day: moment().subtract(90, 'days').format().split('T')[0],
          end_day: moment().add(1, 'day').format().split('T')[0],
        }
        await this.downloadCSV(payload)
      } catch (e) {
        console.log(e)
      }
    },
    async fetchReviewRequestErrors () {
      const payload = {
        page: this.currentPage,
        per_page: this.pageSize,
        sort: this.sortParam,
      }
      const res = await this.listReviewRequestErrors(payload)
      this.total = res.data.total
    },
    onFirstDataRender (params) {
      window.setTimeout(() => {
        params.columnApi.autoSizeColumns(this.columnDefs.map(c => c.field))
      }, 500)
    }
  }
}

</script>

<style>

</style>
